import React, { useEffect, useRef, useMemo } from 'react';
import { useNavigation } from '../../contexts/NavigationContext';
import '../cards/dashboard-glass.css';
import './UniversalHeader.css';

export interface UniversalHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  status?: string;
  statusType?: 'active' | 'closed' | 'pending';
  avatar?: {
    icon?: string;
    imageUrl?: string;
    color?: string;
  };
  coverImage?: string; // URL большого фонового изображения (как в Twitter/Facebook)
  onBack?: () => void;
  onMenu?: () => void;
  onClick?: () => void;
  showMenu?: boolean;
  showBackButton?: boolean; // Контроль отображения кнопки назад
  /** Не вешать «Назад» в контекст (базовый слой вешает страница, напр. ObjectPage с fullscreen-карточками). */
  suppressBackRegistration?: boolean;
  clickable?: boolean;
  className?: string;
  centered?: boolean; // Новое свойство для центрированного режима
  // Новые поля для двухуровневой шапки
  infoRow?: {
    members?: {
      count: number;
      onClick?: () => void;
      getLabel?: (count: number) => string; // Функция для получения названия с правильными окончаниями
    };
    status?: {
      text: string;
      type: 'active' | 'closed' | 'pending';
    };
  };
  /** Дополнительные кнопки/действия в шапке (справа) */
  headerActions?: React.ReactNode;
}

const UniversalHeader: React.FC<UniversalHeaderProps> = ({
  title,
  subtitle,
  status,
  statusType = 'active',
  avatar,
  coverImage,
  onBack,
  onMenu,
  onClick,
  showMenu = false,
  showBackButton = true,
  suppressBackRegistration = false,
  clickable = false,
  className = '',
  centered = false,
  infoRow,
  headerActions
}) => {
  const getStatusClass = () => {
    switch (statusType) {
      case 'closed':
        return 'status-closed';
      case 'pending':
        return 'status-pending';
      default:
        return 'status-active';
    }
  };

  const getAvatarColor = () => {
    if (avatar?.color) return avatar.color;
    switch (statusType) {
      case 'closed':
        return 'var(--theme-text-muted)';
      case 'pending':
        return 'var(--theme-warning)';
      default:
        return 'var(--theme-primary)';
    }
  };

  // Определяем, есть ли валидное изображение
  const hasValidImage = avatar?.imageUrl && typeof avatar.imageUrl === 'string' && avatar.imageUrl.trim().length > 0;
  const hasCoverImage = useMemo(() => {
    return coverImage && typeof coverImage === 'string' && coverImage.trim().length > 0;
  }, [coverImage]);
  
  const headerRef = useRef<HTMLDivElement | null>(null);
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;
  const onMenuRef = useRef(onMenu);
  onMenuRef.current = onMenu;

  // Используем глобальную навигацию для кнопок назад и меню
  const { showBack, hideBack, showMenu: showGlobalMenu, hideMenu } = useNavigation();

  // Регистрируем кнопку «Назад». hideBack только при размонтировании, иначе при ре-рендере
  // (например после закрытия фул-карточки) cleanup затирал бы восстановленный обработчик.
  useEffect(() => {
    return () => {
      if (showBackButton && !suppressBackRegistration) hideBack();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Не вешаем «Назад», если страница сама управляет слоем (suppressBackRegistration, напр. ObjectPage с fullscreen-карточками).
  // В deps только !!onBack (не сама ссылка), иначе inline-колбэки вызывали бы цикл setState в контексте.
  useEffect(() => {
    if (suppressBackRegistration || !onBackRef.current || !showBackButton) return;
    showBack(() => onBackRef.current?.(), !!hasCoverImage);
    // Не вызываем hideBack в cleanup — иначе после закрытия фул-карточки восстановленный
    // обработчик сразу затирался бы при ре-рендере UniversalHeader.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suppressBackRegistration, showBackButton, hasCoverImage, !!onBack]);

  useEffect(() => {
    if (showMenu && onMenuRef.current) {
      showGlobalMenu(() => onMenuRef.current?.(), !!hasCoverImage);
    }
    return () => {
      if (showMenu) hideMenu();
    };
    // onMenu не в deps — используем ref, иначе при каждой смене ссылки (напр. handleMenuClick без useCallback)
    // эффект перезапускался бы, вызывая hideMenu/showMenu и зацикливая setState в контексте
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMenu, hasCoverImage]);

  // Обработчик клика на overlay - проверяем, что клик не на кнопку
  const handleOverlayClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    // Если клик на кнопку или её дочерние элементы - не обрабатываем
    if (target.closest('.back-button') || target.closest('.menu-button')) {
      return;
    }
    onClick?.();
  };


  return (
    <div 
      ref={headerRef}
      className={`universal-header ${centered ? 'centered-header' : ''} ${hasCoverImage ? 'has-cover-image' : ''} ${className}`}
    >
      {/* Фоновое изображение (cover image) */}
      {hasCoverImage && (
        <div className="header-cover-image">
          <img 
            src={coverImage}
            alt=""
            className="cover-image"
            onError={(e) => {
              // Если загрузка cover image не удалась, скрываем его
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const parent = target.parentElement;
              if (parent) {
                parent.style.display = 'none';
              }
              // Убираем класс has-cover-image
              const header = target.closest('.universal-header');
              if (header) {
                header.classList.remove('has-cover-image');
              }
            }}
          />
          <div className="cover-image-overlay"></div>
        </div>
      )}
      
      {/* Основная строка */}
      <div className="header-main-row">
        {onBack && showBackButton && <div className="header-back-spacer" />}
        
        <div className="header-content">
          <div className="header-info">
            <div className="header-title">{title}</div>
            {subtitle && <div className="header-subtitle">{subtitle}</div>}
            {status && (
              <span className={`header-status ${getStatusClass()}`}>
                {status}
              </span>
            )}
          </div>
          
          {clickable && (
            <div className="header-arrow">
              <i className="ri-arrow-right-s-line"></i>
            </div>
          )}
        </div>
        
        {/* Дополнительные действия в шапке (кнопки) */}
        {headerActions && (
          <div className="header-actions-wrap">
            {headerActions}
          </div>
        )}
        
        {/* Аватарка в правом углу */}
        {avatar && (
          <div 
            className={`header-avatar header-avatar-right ${hasValidImage ? 'has-image' : 'no-image'}`}
            style={{ backgroundColor: hasValidImage ? 'transparent' : getAvatarColor() }}
          >
            {/* Иконка - всегда рендерится, показывается когда нет изображения */}
            {avatar.icon && (
              <i className={avatar.icon}></i>
            )}
            {/* Изображение - показывается только если есть валидный URL */}
            {hasValidImage && (
              <img 
                src={avatar.imageUrl}
                alt=""
                className="header-avatar-image"
                onError={(e) => {
                  // Если загрузка изображения не удалась, скрываем изображение и показываем иконку
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    // Меняем класс на no-image чтобы показать иконку
                    parent.classList.remove('has-image');
                    parent.classList.add('no-image');
                    // Устанавливаем фон обратно
                    parent.style.backgroundColor = getAvatarColor();
                  }
                }}
                onLoad={(e) => {
                  // Когда изображение успешно загрузилось, скрываем иконку
                  const target = e.target as HTMLImageElement;
                  const parent = target.parentElement;
                  if (parent) {
                    // Меняем класс на has-image чтобы скрыть иконку
                    parent.classList.remove('no-image');
                    parent.classList.add('has-image');
                    // Убираем фон
                    parent.style.backgroundColor = 'transparent';
                  }
                }}
              />
            )}
          </div>
        )}
        
        {/* Отступ для глобальной кнопки меню */}
        {showMenu && onMenu && <div className="header-menu-spacer" />}
      </div>

      {/* Информационная строка */}
      {infoRow && (
        <div className="header-info-row">
          {infoRow.members && (
            <div
              className="info-members"
              role={infoRow.members.onClick ? 'button' : undefined}
              tabIndex={infoRow.members.onClick ? 0 : undefined}
              onClick={infoRow.members.onClick}
              onKeyDown={
                infoRow.members.onClick
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        infoRow.members?.onClick?.();
                      }
                    }
                  : undefined
              }
            >
              <i className="ri-group-line"></i>
              <span>{infoRow.members.count} {infoRow.members.getLabel ? infoRow.members.getLabel(infoRow.members.count) : 'участников'}</span>
            </div>
          )}
          
          {infoRow.status && (
            <div className={`info-status status-${infoRow.status.type}`}>
              <i className={`ri-${infoRow.status.type === 'active' ? 'play' : infoRow.status.type === 'closed' ? 'stop' : 'pause'}-circle-line`}></i>
              <span>{infoRow.status.text}</span>
            </div>
          )}
        </div>
      )}

      {/* Кликабельный слой — после контента, иначе main-row перехватывает нажатия */}
      {clickable && onClick && (
        <div
          className={`header-click-overlay ${onBack ? 'has-back-button' : ''} ${showMenu && onMenu ? 'has-menu-button' : ''} ${headerActions ? 'has-header-actions' : ''}`}
          onClick={handleOverlayClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick?.();
            }
          }}
          aria-label="Открыть"
        />
      )}
    </div>
  );
};

export default UniversalHeader;
